import React, { useEffect, useMemo, useState } from "react";
import { METRIC_EVENT_COUNT } from "constants/metrics";
import { DIMENSION_DATE, DIMENSION_EVENT_NAME } from "constants/dimensions";
import {
  DIMENSION_ITEM_BRAND,
  DIMENSION_ITEM_CATEGORY,
  DIMENSION_ITEM_ID,
  DIMENSION_ITEM_ID_TEMP,
  DIMENSION_ITEM_NAME,
  DIMENSION_ITEM_SOURCE,
} from "constants/customDimensions";
import { get, isEmpty, orderBy, sum } from "lodash";
import { Card, Col, Row, Spin } from "antd";
import { GRID_GUTTER } from "constants/ui";
import LineChart from "components/charts/LineChart";
import {
  getBatchReports,
  getContentTypeName,
  reviseReport,
  reviseTimeSeriesData,
} from "services/gaService";
import {
  buildInListFilter,
  buildStringFilter,
  FILTER_EVENT_PLUG_CONTENT,
} from "services/gaDimensionsService";
import { EVENT_PLUG_ITEM } from "constants/customEvents";
import PieChart from "components/charts/PieChart";
import ContentType from "components/ContentType";
import Scorecard from "components/charts/Scorecard";
import {
  TAG_BOOKMARK,
  TAG_DOMAIN,
  TAG_FACEBOOK,
  TAG_HIGHLIGHT,
  TAG_RSS,
  TAG_TWITTER,
} from "constants/contentTags";
import TableChart from "components/charts/TableChart";
import ContentLink from "components/ContentLink";
import ChartTitle from "components/ChartTitle";
import { useReportContext } from "components/ReportContext/ReportContextProvider";
import { v4 as uuid } from "uuid";
import { alertUnknownError } from "services/notificationService";
import { useFormatNumber } from "hooks/useFormatNumber";
const buildFilters = (filters, notFilters) => {
  return {
    andGroup: {
      expressions: [
        ...(filters || []),
        buildInListFilter(DIMENSION_ITEM_CATEGORY, [
          "url",
          "domain",
          "highlight",
          "rss",
          "twitter",
          "facebook",
        ]),
      ],
    },
  };
};
const syncNamesOptions = {
  idKey: DIMENSION_ITEM_ID,
  nameKey: DIMENSION_ITEM_NAME,
  typeKey: DIMENSION_ITEM_CATEGORY,
};
const PlugContents = () => {
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { dateStrings } = useReportContext();
  const formatNumber = useFormatNumber();
  const reportRequests = useMemo(() => {
    if (!dateStrings || !dateStrings[0] || !dateStrings[1]) return;

    return [
      // Content Views, Plug, Plug events
      {
        dateRanges: [
          {
            startDate: dateStrings[0],
            endDate: dateStrings[1],
          },
        ],
        dimensions: [
          {
            name: DIMENSION_DATE,
          },
          {
            name: DIMENSION_ITEM_SOURCE,
          },
          {
            name: DIMENSION_ITEM_CATEGORY,
          },
        ],
        metrics: [
          {
            name: METRIC_EVENT_COUNT,
          },
        ],
        dimensionFilter: buildFilters([
          buildStringFilter(DIMENSION_EVENT_NAME, EVENT_PLUG_ITEM),
        ]),
      },

      {
        dateRanges: [
          {
            startDate: dateStrings[0],
            endDate: dateStrings[1],
          },
        ],
        dimensions: [
          {
            name: DIMENSION_ITEM_ID,
          },
          // {
          //   name: DIMENSION_ITEM_NAME,
          // },
          {
            name: DIMENSION_ITEM_CATEGORY,
          },
          {
            name: DIMENSION_ITEM_BRAND,
          },
        ],
        metrics: [
          {
            name: METRIC_EVENT_COUNT,
          },
        ],
        dimensionFilter: buildFilters([FILTER_EVENT_PLUG_CONTENT]),
        limit: 100,
        offset: 0,
      },
    ];
  }, [dateStrings]);

  useEffect(() => {
    if (!reportRequests) return;

    const loadData = async () => {
      setIsLoading(true);
      try {
        const data = await getBatchReports(reportRequests);
        setReports(get(data, "reports", []));
      } catch (error) {
        alertUnknownError();
        setReports([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [reportRequests]);

  const commonReportData = useMemo(() => {
    return reviseReport(reports[0]);
  }, [reports]);

  const plugContentsReportData = useMemo(() => {
    return reviseReport(reports[1]);
  }, [reports]);

  const eventTrendTimeSeriesData = useMemo(() => {
    if (isEmpty(commonReportData)) return [];

    const dataKeysMap = {};
    const rowsGroupedByDate = commonReportData.rows.reduce(
      (result, current) => {
        const date = current[DIMENSION_DATE];
        const category = current[DIMENSION_ITEM_CATEGORY];
        if (!result[date]) result[date] = {};

        result[date][DIMENSION_DATE] = date;
        result[date][category] = sum([
          result[date][category],
          current[METRIC_EVENT_COUNT],
        ]);
        result[date][METRIC_EVENT_COUNT] = sum([
          result[date][METRIC_EVENT_COUNT],
          current[METRIC_EVENT_COUNT],
        ]);
        dataKeysMap[category] = category;
        return result;
      },
      {}
    );

    // return orderBy(Object.values(rowsGroupedByDate), [DIMENSION_DATE]);
    return reviseTimeSeriesData(Object.values(rowsGroupedByDate), {
      dateKey: DIMENSION_DATE,
      startDate: dateStrings[0],
      endDate: dateStrings[1],
      dataKeys: [...Object.keys(dataKeysMap), METRIC_EVENT_COUNT],
    });
  }, [commonReportData, dateStrings]);

  const plugCardCount = useMemo(() => {
    if (isEmpty(eventTrendTimeSeriesData)) return 0;

    return sum(eventTrendTimeSeriesData.map((item) => item[TAG_BOOKMARK]));
  }, [eventTrendTimeSeriesData]);

  const plugHighlightCount = useMemo(() => {
    if (isEmpty(eventTrendTimeSeriesData)) return 0;

    return sum(eventTrendTimeSeriesData.map((item) => item[TAG_HIGHLIGHT]));
  }, [eventTrendTimeSeriesData]);

  const plugNewsCount = useMemo(() => {
    if (isEmpty(eventTrendTimeSeriesData)) return 0;

    return sum(eventTrendTimeSeriesData.map((item) => item[TAG_RSS]));
  }, [eventTrendTimeSeriesData]);

  const plugCompanyCount = useMemo(() => {
    if (isEmpty(eventTrendTimeSeriesData)) return 0;

    return sum(eventTrendTimeSeriesData.map((item) => item[TAG_DOMAIN]));
  }, [eventTrendTimeSeriesData]);

  const plugTwitterCount = useMemo(() => {
    if (isEmpty(eventTrendTimeSeriesData)) return 0;

    return sum(eventTrendTimeSeriesData.map((item) => item[TAG_TWITTER]));
  }, [eventTrendTimeSeriesData]);

  const plugFacebookCount = useMemo(() => {
    if (isEmpty(eventTrendTimeSeriesData)) return 0;

    return sum(eventTrendTimeSeriesData.map((item) => item[TAG_FACEBOOK]));
  }, [eventTrendTimeSeriesData]);

  const plugByTypesPieChartData = useMemo(() => {
    if (isEmpty(commonReportData)) return [];

    const rowsGroupedByCategories = commonReportData.rows.reduce(
      (result, current) => {
        const category = current[DIMENSION_ITEM_CATEGORY];
        if (!result[category]) result[category] = {};

        result[category][DIMENSION_ITEM_CATEGORY] =
          getContentTypeName(category);
        result[category][METRIC_EVENT_COUNT] = sum([
          result[category][METRIC_EVENT_COUNT],
          current[METRIC_EVENT_COUNT],
        ]);
        return result;
      },
      {}
    );

    return orderBy(Object.values(rowsGroupedByCategories), [DIMENSION_DATE]);
  }, [commonReportData]);

  const plugContentsTableData = useMemo(() => {
    if (isEmpty(plugContentsReportData)) return [];

    const rowsGroupedByDate = plugContentsReportData.rows.reduce(
      (result, current) => {
        const itemId = current[DIMENSION_ITEM_ID];
        if (!result[itemId]) result[itemId] = {};
        result[itemId][DIMENSION_ITEM_ID] = itemId;
        result[itemId][DIMENSION_ITEM_NAME] = current[DIMENSION_ITEM_NAME];
        result[itemId][DIMENSION_ITEM_BRAND] = current[DIMENSION_ITEM_BRAND];
        result[itemId][DIMENSION_ITEM_CATEGORY] =
          current[DIMENSION_ITEM_CATEGORY];
        result[itemId][METRIC_EVENT_COUNT] = sum([
          result[itemId][METRIC_EVENT_COUNT],
          current[METRIC_EVENT_COUNT],
        ]);
        return result;
      },
      {}
    );

    return orderBy(
      Object.values(plugContentsReportData.rows),
      [METRIC_EVENT_COUNT],
      ["desc"]
    ).map((item) => ({ ...item, [DIMENSION_ITEM_ID_TEMP]: uuid() }));
  }, [plugContentsReportData]);

  return (
    <Spin spinning={isLoading}>
      <ChartTitle>Plug Trend</ChartTitle>
      <Row gutter={GRID_GUTTER}>
        <Col xs={24} lg={12} className="mb-8">
          <Card size="small">
            <LineChart
              xAxisDataKey={DIMENSION_DATE}
              data={eventTrendTimeSeriesData}
              lines={[
                {
                  name: "Event",
                  dataKey: METRIC_EVENT_COUNT,
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12} className="mb-8">
          <Row gutter={GRID_GUTTER}>
            <Col span={12} className="mb-8">
              <Card size="small">
                <Scorecard
                  title="Cards"
                  value={formatNumber(plugCardCount)}
                  chartProps={{
                    data: eventTrendTimeSeriesData,
                    lines: [
                      {
                        name: "Cards",
                        dataKey: TAG_BOOKMARK,
                      },
                    ],
                  }}
                />
              </Card>
            </Col>
            <Col span={12} className="mb-8">
              <Card size="small">
                <Scorecard
                  title="Newsletters"
                  value={formatNumber(plugHighlightCount)}
                  chartProps={{
                    data: eventTrendTimeSeriesData,
                    lines: [
                      {
                        name: "Event",
                        dataKey: TAG_HIGHLIGHT,
                      },
                    ],
                  }}
                />
              </Card>
            </Col>
            <Col span={12} className="mb-8">
              <Card size="small">
                <Scorecard
                  title="News"
                  value={formatNumber(plugNewsCount)}
                  chartProps={{
                    data: eventTrendTimeSeriesData,
                    lines: [
                      {
                        name: "Event",
                        dataKey: TAG_RSS,
                      },
                    ],
                  }}
                />
              </Card>
            </Col>
            <Col span={12} className="mb-8">
              <Card size="small">
                <Scorecard
                  title="Companies"
                  value={formatNumber(plugCompanyCount)}
                  chartProps={{
                    data: eventTrendTimeSeriesData,
                    lines: [
                      {
                        name: "Event",
                        dataKey: TAG_DOMAIN,
                      },
                    ],
                  }}
                />
              </Card>
            </Col>
            <Col span={12} className="mb-8">
              <Card size="small">
                <Scorecard
                  title="Twitter"
                  value={formatNumber(plugTwitterCount)}
                  chartProps={{
                    data: eventTrendTimeSeriesData,
                    lines: [
                      {
                        name: "Event",
                        dataKey: TAG_TWITTER,
                      },
                    ],
                  }}
                />
              </Card>
            </Col>
            <Col span={12} className="mb-8">
              <Card size="small">
                <Scorecard
                  title="Facebook"
                  value={formatNumber(plugFacebookCount)}
                  chartProps={{
                    data: eventTrendTimeSeriesData,
                    lines: [
                      {
                        name: "Event",
                        dataKey: TAG_FACEBOOK,
                      },
                    ],
                  }}
                />
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={GRID_GUTTER}>
        <Col xs={24} lg={12}>
          <ChartTitle>Top Plugged Contents</ChartTitle>
          <Card size="small" className="lg:h-full">
            <TableChart
              rowKey={DIMENSION_ITEM_ID_TEMP}
              dataSource={plugContentsTableData}
              syncNames={syncNamesOptions}
              columns={[
                {
                  title: "Content",
                  dataIndex: DIMENSION_ITEM_ID,
                  key: DIMENSION_ITEM_ID,
                  ellipsis: true,
                  render: (_, record) => (
                    <ContentLink
                      id={record[DIMENSION_ITEM_ID]}
                      name={record[DIMENSION_ITEM_NAME]}
                      tag={record[DIMENSION_ITEM_CATEGORY]}
                      ownerDomain={record[DIMENSION_ITEM_BRAND]}
                    />
                  ),
                },
                {
                  title: "Type",
                  dataIndex: DIMENSION_ITEM_CATEGORY,
                  key: DIMENSION_ITEM_CATEGORY,
                  render: (value) => <ContentType tag={value} />,
                  width: 80,
                },
                {
                  title: "Plug",
                  dataIndex: METRIC_EVENT_COUNT,
                  key: METRIC_EVENT_COUNT,
                  width: 100,
                  align: "right",
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12} className="mb-8">
          <ChartTitle>Plug Content By Types</ChartTitle>
          <Card size="small" className="lg:h-full">
            <div className="h-80">
              <PieChart
                data={plugByTypesPieChartData}
                dataKey={METRIC_EVENT_COUNT}
                nameKey={DIMENSION_ITEM_CATEGORY}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </Spin>
  );
};

PlugContents.propTypes = {};

export default PlugContents;
